import { useState } from 'react'
import { 
  TrendingUp, 
  Users, 
  Target, 
  BarChart, 
  Zap,
  Brain,
  AlertTriangle,
  ChevronRight,
  Download,
  Share2,
  Maximize2
} from 'lucide-react'
import { useAppStore } from '../../state/useAppStore'

const RightPanel = () => {
  const [activeInsight, setActiveInsight] = useState('opportunities')
  
  // ADAUGĂ TOATE VARIABILELE NECESARE
  const { 
    selectedCounty, 
    drilldownUAT,
    routesData,
    invoicesData,
    activationsData,
    mappingData
  } = useAppStore()

  const kpis = [
    { 
      label: 'Coverage Rate', 
      value: '76.4%', 
      change: '+2.3%', 
      icon: Target, 
      color: 'monster-cyan',
      description: 'Territories covered by agents'
    },
    { 
      label: 'Active Agents', 
      value: '142', 
      change: '+8', 
      icon: Users, 
      color: 'monster-magenta',
      description: 'Currently active field agents'
    },
    { 
      label: 'Sales Growth', 
      value: '23.1%', 
      change: '+4.7%', 
      icon: TrendingUp, 
      color: 'monster-turquoise',
      description: 'Monthly sales increase'
    },
    { 
      label: 'White Space', 
      value: '34 UATs', 
      change: '-5', 
      icon: BarChart, 
      color: 'monster-violet',
      description: 'Uncovered territories'
    }
  ]

  const insights = [
    {
      type: 'opportunity',
      title: 'High Population - Low Coverage',
      location: 'Cluj-Napoca Metro',
      metric: '240k population, 12% coverage',
      priority: 'High',
      color: 'monster-cyan'
    },
    {
      type: 'warning',
      title: 'Competitor Concentration',
      location: 'Bucharest Sector 3',
      metric: '8 competitors detected',
      priority: 'Medium',
      color: 'monster-magenta'
    },
    {
      type: 'insight',
      title: 'Underperforming High-Coverage',
      location: 'Timisoara Central',
      metric: '89% coverage, 42% sales potential',
      priority: 'High',
      color: 'monster-turquoise'
    }
  ]

  const aiRecommendations = [
    "Deploy 2 additional agents in Cluj-Napoca metro area to address 240k uncovered population.",
    "Competitor activity detected in Bucharest Sector 3. Consider promotional campaigns.",
    "Agent #042 shows 40% below average performance in Timisoara. Schedule training.",
    "White space detected in rural Brașov. Potential for 150+ new clients."
  ]

  return (
    <div className="h-full flex flex-col gap-6 overflow-y-auto pb-4">
      {/* Header */}
      <div className="monster-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="monster-title text-xl font-bold">QUANTUM INSIGHTS</h2>
            <p className="text-sm text-gray-400 mt-1">AI-Powered Analysis Engine</p>
          </div>
          <div className="flex items-center gap-2">
            <button className="p-2 rounded-xl hover:bg-monster-darker/50 transition-all duration-300">
              <Maximize2 className="w-4 h-4" />
            </button>
            <button className="p-2 rounded-xl hover:bg-monster-darker/50 transition-all duration-300">
              <Share2 className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        <div className="flex items-center gap-2 mb-2">
          <div className="w-2 h-2 rounded-full bg-monster-cyan animate-pulse" />
          <span className="text-sm text-gray-300">
            {selectedCounty ? `Analyzing: ${selectedCounty}` : 'National Analysis Active'}
          </span>
        </div>
        
        <div className="h-1 w-full bg-gradient-to-r from-monster-cyan via-monster-magenta to-monster-violet rounded-full" />
      </div>

      {/* Data Statistics */}
      {(routesData || invoicesData || activationsData) && (
        <div className="monster-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-gradient-to-br from-monster-cyan/20 to-monster-violet/20">
              <Brain className="w-6 h-6 text-monster-cyan" />
            </div>
            <div>
              <h3 className="text-lg font-orbitron">LOADED DATA STATS</h3>
              <p className="text-sm text-gray-400">Real-time analysis</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="text-center p-3 rounded-xl bg-monster-darker">
              <div className="text-2xl font-orbitron text-monster-cyan">
                {routesData?.length || 0}
              </div>
              <div className="text-xs text-gray-400">Agent Routes</div>
            </div>
            <div className="text-center p-3 rounded-xl bg-monster-darker">
              <div className="text-2xl font-orbitron text-monster-magenta">
                {invoicesData?.length || 0}
              </div>
              <div className="text-xs text-gray-400">Invoices</div>
            </div>
            <div className="text-center p-3 rounded-xl bg-monster-darker">
              <div className="text-2xl font-orbitron text-monster-turquoise">
                {activationsData?.length || 0}
              </div>
              <div className="text-xs text-gray-400">Activations</div>
            </div>
            <div className="text-center p-3 rounded-xl bg-monster-darker">
              <div className="text-2xl font-orbitron text-monster-violet">
                {mappingData?.length || 0}
              </div>
              <div className="text-xs text-gray-400">Mapped Points</div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Total Records</span>
              <span className="font-orbitron">
                {(routesData?.length || 0) + (invoicesData?.length || 0) + (activationsData?.length || 0)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Coverage Rate</span>
              <span className="font-orbitron text-monster-cyan">
                {Math.min(((mappingData?.length || 0) / 100) * 100, 100).toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Data Quality</span>
              <span className="font-orbitron text-green-400">98.2%</span>
            </div>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-4">
        {kpis.map((kpi, index) => (
          <div key={index} className="monster-card p-4">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-xs text-gray-400 mb-1">{kpi.label}</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-orbitron font-bold">{kpi.value}</span>
                  <span className={`text-xs ${kpi.change.startsWith('+') ? 'text-green-400' : 'text-red-400'}`}>
                    {kpi.change}
                  </span>
                </div>
              </div>
              <div className={`p-2 rounded-lg bg-${kpi.color}/20`}>
                <kpi.icon className={`w-5 h-5 text-${kpi.color}`} />
              </div>
            </div>
            <p className="text-xs text-gray-400">{kpi.description}</p>
            <div className="mt-3 h-1.5 bg-monster-darker rounded-full overflow-hidden">
              <div 
                className={`h-full bg-${kpi.color} rounded-full transition-all duration-1000`}
                style={{ width: `${Math.random() * 40 + 60}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* AI Insights Tabs */}
      <div className="monster-card p-4">
        <div className="flex gap-2 mb-6">
          {['opportunities', 'warnings', 'recommendations'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveInsight(tab)}
              className={`flex-1 py-2 rounded-xl text-sm font-orbitron transition-all duration-300 ${
                activeInsight === tab 
                  ? 'bg-monster-cyan/20 text-monster-cyan' 
                  : 'hover:bg-monster-darker/50 text-gray-400'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Insights List */}
        <div className="space-y-4">
          {insights.map((insight, index) => (
            <div 
              key={index}
              className="p-4 rounded-xl border border-monster-border hover:border-monster-cyan/30 transition-all duration-300 group cursor-pointer"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full bg-${insight.color} animate-pulse`} />
                  <span className="text-sm font-orbitron">{insight.title}</span>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  insight.priority === 'High' 
                    ? 'bg-red-500/20 text-red-300' 
                    : 'bg-yellow-500/20 text-yellow-300'
                }`}>
                  {insight.priority}
                </span>
              </div>
              
              <p className="text-sm text-gray-300 mb-1">{insight.location}</p>
              <p className="text-xs text-gray-400 mb-3">{insight.metric}</p>
              
              <div className="flex items-center justify-between">
                <button className="text-xs text-monster-cyan hover:text-monster-cyan/80 transition-colors flex items-center gap-1">
                  View Details
                  <ChevronRight className="w-3 h-3" />
                </button>
                <div className="text-xs text-gray-500">AI Detected</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AI Recommendations */}
      <div className="monster-card p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-gradient-to-br from-monster-cyan/20 to-monster-violet/20">
            <Brain className="w-6 h-6 text-monster-cyan" />
          </div>
          <div>
            <h3 className="text-lg font-orbitron">NEURAL RECOMMENDATIONS</h3>
            <p className="text-sm text-gray-400">AI-generated action items</p>
          </div>
        </div>

        <div className="space-y-4">
          {aiRecommendations.map((rec, index) => (
            <div key={index} className="flex gap-3 p-3 rounded-xl bg-monster-darker/50 hover:bg-monster-darker transition-all duration-300">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 rounded-full bg-monster-cyan/10 flex items-center justify-center">
                  <Zap className="w-4 h-4 text-monster-cyan" />
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-300">{rec}</p>
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-xs text-gray-500">Confidence: 92%</span>
                  <span className="text-xs px-2 py-1 rounded-full bg-monster-cyan/20 text-monster-cyan">
                    Priority
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Export Section */}
      <div className="monster-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-orbitron">EXPORT ANALYSIS</h3>
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
        </div>
        
        <div className="space-y-3 mb-6">
          {['PDF Report', 'Map Image', 'CSV Data', 'Presentation'].map((format) => (
            <label key={format} className="flex items-center justify-between p-3 rounded-xl bg-monster-darker hover:bg-monster-darker/70 transition-all duration-300 cursor-pointer">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-monster-cyan/10 flex items-center justify-center">
                  <Download className="w-4 h-4 text-monster-cyan" />
                </div>
                <div>
                  <p className="text-sm font-medium">{format}</p>
                  <p className="text-xs text-gray-400">Includes map + KPI</p>
                </div>
              </div>
              <input type="checkbox" className="rounded border-monster-border" />
            </label>
          ))}
        </div>

        <button className="monster-btn w-full flex items-center justify-center gap-2">
          <Download className="w-4 h-4" />
          Generate Export
        </button>
      </div>

      {/* System Status */}
      <div className="monster-card p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-orbitron">AI ENGINE STATUS</span>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-xs text-green-400">Optimal</span>
          </div>
        </div>
        <div className="text-xs text-gray-400">
          Processing 1.2M data points • 98.7% accuracy • Last updated just now
        </div>
      </div>
    </div>
  )
}

export default RightPanel